import React, { Component } from 'react';
import PropTypes from 'prop-types';
import qs from 'qs';
import InfiniteList from './InfiniteList';
import cable from '../cable';
import axios from '../lib/axios';
import ReactRailsUJS from '../lib/react-rails';

// Similar to InfiniteList but listens to changes to the URL and
// everytime it's updated it makes a request to get data from the server
export default class InfiniteFetchList extends Component {
  static propTypes = {
    apiUrl: PropTypes.string.isRequired,
    perPage: PropTypes.number.isRequired,
    itemRenderer: PropTypes.func.isRequired,
    purgeDuplicateKey: PropTypes.string,
    loadingElement: PropTypes.element,
    noResultsElement: PropTypes.element,
    loadingMessage: PropTypes.string,
    noResultsMessage: PropTypes.string,
    reachedEndMessage: PropTypes.any,
    subscribeTo: PropTypes.shape({
      channel: PropTypes.string,
      community_id: PropTypes.string,
    }),
    onLoad: PropTypes.func,
    extraParams: PropTypes.object,
    loadingCardsAnimation: PropTypes.bool,
  };

  static defaultProps = {
    purgeDuplicateKey: null,
    loadingCardsAnimation: true,
    loadingMessage: 'Loading…',
    noResultsMessage: 'Oh no! Seems like there is no content in this context.',
    reachedEndMessage: 'Looks like you’ve reached the end of the feed.',
    extraParams: {},
    onLoad: () => {},
    subscribeTo: null,
  };

  constructor(props) {
    super(props);

    this.state = {
      items: [],
      currentPage: 1,
      loading: false,
      reachedEnd: false,
    };
  }

  subscribe() {
    this.subscription = cable.subscriptions.create(
      {
        channel: this.props.subscribeTo.channel,
        community_id: this.props.subscribeTo.community_id,
      },
      {
        received: card => {
          const items = this.state.items.slice();
          items.unshift(card);
          this.setState({ items });
        },
      }
    );
  }

  unsubscribe() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  handleLoad = callback => {
    const params = qs.parse(window.location.search.slice(1));
    Object.assign(params, this.props.extraParams, { page: this.state.currentPage + 1, perPage: this.props.perPage });

    if (this.request) this.request.cancel('Canceled');

    const { CancelToken } = axios;
    const source = CancelToken.source();

    this.request = source;

    this.setState({ loading: true }, () => {
      axios.get(this.props.apiUrl, {
        params,
        paramsSerializer: (p) => qs.stringify(p, { arrayFormat: 'brackets', depth: 2 }),
        cancelToken: source.token,
      }).then((response) => {
        this.request = null;
        const { data } = response;
        let newItems = [];

        if (this.props.purgeDuplicateKey) {
          newItems = this.state.items.concat(data).reduce(
            (uniq, item) => (
              uniq.map(i => i[this.props.purgeDuplicateKey]).includes(item[this.props.purgeDuplicateKey]) ? uniq : [...uniq, item]
            ), []
          );
        } else {
          newItems = this.state.items.concat(data);
        }
        this.setState(
          {
            reachedEnd: data.length < this.props.perPage,
            loading: false,
            items: newItems,
            currentPage: this.state.currentPage + 1,
          },
          () => {
            if (callback) {
              callback();
            }
            this.props.onLoad();
          }
        );
      }).catch((thrown) => {
        if (axios.isCancel(thrown)) {
          // No-op, but at least we catch this and prevent an unecessary JS error on Sentry
        } else {
          throw thrown;
        }
      });
    });
  };

  handleParamsChange = () => {
    this.setState(
      {
        reachedEnd: false,
        loading: true,
        currentPage: 0,
        items: [],
      },
      this.handleLoad
    );
  };

  componentDidMount() {
    this.unlistenParamsChange = router.listen(this.handleParamsChange);
    this.handleParamsChange();
    if (this.props.subscribeTo !== null) {
      this.subscribe();
    }
  }

  componentWillUnmount() {
    this.unlistenParamsChange();
    if (this.props.subscribeTo !== null) {
      this.unsubscribe();
    }
  }

  renderItem = (index, key) => this.props.itemRenderer(this.state.items[index], key);

  renderCardLoading = (n) => {
    const repetitions = [];
    for (let step = 0; step < (n || 1); step++) {
      repetitions.push(
        <div key={`loading-${step}`} className="u-relative u-flex u-flex-col u-border u-shadow u-rounded u-bg-white u-py-3 u-px-5 u-mb-4">
          <div className="u-flex u-mb-2">
            <div className="shimmer u-rounded-full u-w-11 u-h-11 u-mr-2" />
            <div className="u-flex u-flex-1 u-flex-col">
              <div className="shimmer u-rounded u-h-3 u-w-1/5 u-mb-1" />
              <div className="shimmer u-rounded u-h-3 u-w-1/3 u-mb-1" />
              <div className="shimmer u-rounded u-h-3 u-w-1/3" />
            </div>
          </div>
          <div className="shimmer u-rounded u-h-5 u-w-1/2 u-mb-1" />
          <div className="shimmer u-rounded u-h-4 u-w-full u-mb-1" />
          <div className="shimmer u-rounded u-h-4 u-w-full" />
        </div>
      );
    }
    return (
      <div>
        {repetitions}
      </div>
    );
  };

  render() {
    // If there is only one page of content, don't show the end of feed message
    const reachedEndMessage = this.state.currentPage > 1 ? this.props.reachedEndMessage : '';
    let noResultsElement; let
      noResultsMessage;

    if (this.props.noResultsElement && this.props.loadingElement) {
      noResultsElement = this.state.loading ? this.props.loadingElement : this.props.noResultsElement;
      noResultsMessage = null;
    } else if (this.props.loadingCardsAnimation) {
      if (this.props.noResultsElement) {
        noResultsElement = this.state.loading ? this.renderCardLoading(5) : this.props.noResultsElement;
        noResultsMessage = null;
      } else {
        noResultsElement = this.state.loading ? this.renderCardLoading(5) : <div>{this.props.noResultsMessage}</div>;
        noResultsMessage = null;
      }
    } else {
      noResultsMessage = this.state.loading ? this.props.loadingMessage : this.props.noResultsMessage;
      noResultsElement = null;
    }
    return (
      <InfiniteList
        {...this.props}
        loadingElement={noResultsElement}
        noResultsMessage={noResultsMessage}
        noResultsElement={noResultsElement}
        reachedEndMessage={reachedEndMessage}
        onLoad={this.handleLoad}
        itemRenderer={this.renderItem}
        length={this.state.items.length}
        reachedEnd={this.state.reachedEnd}
      />
    );
  }
}
